import { contractAddressFromSpaceId, isSpaceStreamId, isUserId } from '@towns-protocol/sdk'
import { axiosClient } from 'api/apiClient'
import { env } from 'utils'

export type ImageSize = `${number}x${number}` | `${number}x` | `x${number}`

export function getImageUrlFromStreamMetadata(resourceId: string, size?: ImageSize) {
    const imageServiceUrl = new URL(env.VITE_RIVER_STREAM_METADATA_URL)
    if (isSpaceStreamId(resourceId)) {
        const contractAddress = contractAddressFromSpaceId(resourceId)
        imageServiceUrl.pathname = `/space/${contractAddress}/image`
        if (size) {
            imageServiceUrl.searchParams.set('size', size)
        }
        return imageServiceUrl.toString()
    }
    if (isUserId(resourceId)) {
        imageServiceUrl.pathname = `/user/${resourceId}/image`
        if (size) {
            imageServiceUrl.searchParams.set('size', size)
        }
        return imageServiceUrl.toString()
    }
}

export async function fetchSpaceImage(spaceId: string | undefined, size?: ImageSize) {
    if (!spaceId || !isSpaceStreamId(spaceId)) {
        throw new Error('Invalid space id')
    }
    const imageServiceUrl = getImageUrlFromStreamMetadata(spaceId, size)
    if (!imageServiceUrl) {
        throw new Error('Invalid image service url')
    }

    const response = await fetch(imageServiceUrl)
    if (response.status !== 200) {
        throw new Error('Network response was not ok')
    }

    return { imageUrl: response.url }
}

export async function fetchUserProfileImage(userId: string | undefined, size?: ImageSize) {
    if (!userId) {
        throw new Error('Invalid user id')
    }
    const imageServiceUrl = getImageUrlFromStreamMetadata(userId)
    if (!imageServiceUrl) {
        throw new Error('Invalid image service url')
    }

    const response = await fetch(imageServiceUrl)
    if (response.status !== 200) {
        throw new Error('Network response was not ok')
    }

    return { imageUrl: response.url }
}

type RefreshCacheResponse = { ok: boolean; invalidationId?: string }

export async function refreshSpaceCache(spaceId: string): Promise<RefreshCacheResponse> {
    const route = new URL(env.VITE_RIVER_STREAM_METADATA_URL)
    const contractAddress = contractAddressFromSpaceId(spaceId)
    route.pathname = `/space/${contractAddress}/refresh`
    try {
        const { data } = await axiosClient.get<RefreshCacheResponse>(route.toString())
        return { ok: data.ok, invalidationId: data.invalidationId }
    } catch (e) {
        return { ok: false }
    }
}

export async function refreshUserImageCache(userId: string): Promise<RefreshCacheResponse> {
    const route = new URL(env.VITE_RIVER_STREAM_METADATA_URL)
    route.pathname = `/user/${userId}/refresh`
    route.searchParams.set('target', 'image')
    try {
        const { data } = await axiosClient.get<RefreshCacheResponse>(route.toString())
        return { ok: data.ok, invalidationId: data.invalidationId }
    } catch (e) {
        return { ok: false }
    }
}

export async function refreshUserBioCache(userId: string): Promise<RefreshCacheResponse> {
    const route = new URL(env.VITE_RIVER_STREAM_METADATA_URL)
    route.pathname = `/user/${userId}/refresh`
    route.searchParams.set('target', 'bio')
    try {
        const { data } = await axiosClient.get<RefreshCacheResponse>(route.toString())
        return { ok: data.ok, invalidationId: data.invalidationId }
    } catch (e) {
        return { ok: false }
    }
}

type RefreshStatusResponse = { status: 'completed' | 'pending' | 'unset' | 'error' }

export async function getRefreshStatus(invalidationId: string) {
    const route = new URL(env.VITE_RIVER_STREAM_METADATA_URL)
    route.pathname = `/refreshStatus/${invalidationId}`
    const { data } = await axiosClient.get<RefreshStatusResponse>(route.toString())
    return data
}

// Cloudfront invalidations takes 10 to 100 seconds to complete
export function getRefreshStatusRetryDelay(attempt: number) {
    return Math.min(4_000 * Math.pow(1.5, attempt), 15_000)
}

export async function waitForInvalidation(invalidationId: string) {
    let attempt = 0
    while (attempt < 8) {
        const { status } = await getRefreshStatus(invalidationId)
        if (status === 'completed') {
            return status
        }
        await new Promise((resolve) => setTimeout(resolve, getRefreshStatusRetryDelay(attempt)))
        attempt++
    }
    throw new Error('Invalidation not completed')
}

export const buildSpaceMetadataUrl = (spaceAddress: string) => {
    const url = new URL(env.VITE_RIVER_STREAM_METADATA_URL)
    url.pathname = `/space/${spaceAddress}`
    return url.toString()
}
